import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { DashboardFiltersFacade } from '../../domain/+state/dashboard-filters.facade';
import { DashboardFacade } from '../../domain/+state/dashboard.facade';
import {
  ConversionMetrics,
  ConversionMetricsDataVm,
  ConversionMetricsVm,
} from '../../domain/model/conversion-metrics.model';
import { DashboardFilters } from '../../domain/model/dashboard-filters.model';
import { DashboardVmState } from '../../domain/model/dashboard-vm.model';
import {
  comparisonLabel,
  CONVERSION_CARD_ORDER,
  ConversionSelection,
  sameConversionSelection,
  toConversionCardVm,
  toConversionSelection,
} from '../../shared/conversion-metrics.helper';
import { companyToday, resolveDateRange } from '../../shared/dashboard-filters.helper';
import { clampToNow, errorVm, loadingVm } from '../../shared/dashboard-vm.helper';

const LOADING_VM: ConversionMetricsVm = loadingVm<ConversionMetricsDataVm>();

/** Everything the template reads is assembled here: no arithmetic and no string building in the markup. */
function toDataVm(metrics: ConversionMetrics, now: Date): ConversionMetricsVm {
  // From the response's own window, never the filter bar.
  const label = comparisonLabel(metrics.comparison);
  return {
    state: DashboardVmState.DATA,
    asOfUtc: clampToNow(metrics.asOfUtc, now),
    errorMessage: null,
    data: {
      cards: CONVERSION_CARD_ORDER.map((card) => toConversionCardVm(card, metrics.cards[card], label)),
    },
  };
}

/**
 * Screen facade of the conversion metrics page: one request per distinct `(from, to, scope, period)` and a
 * precomputed view model. Component-provided; lives as long as the screen.
 */
@Injectable()
export class ConversionMetricsFacade {
  /** loading → data | error per request. */
  readonly vm$: Observable<ConversionMetricsVm>;

  constructor(
    private readonly dashboardFacade: DashboardFacade,
    private readonly filtersFacade: DashboardFiltersFacade,
  ) {
    // The range is resolved from the same emission as the period: pairing `filters$` with `dateRange$` would
    // emit the new period with the old range first, a request the server rejects.
    this.vm$ = combineLatest([this.filtersFacade.filters$, this.filtersFacade.now$]).pipe(
      map(([filters, now]: [DashboardFilters, Date]) =>
        toConversionSelection(filters, resolveDateRange(filters, companyToday(now))),
      ),
      filter((selection): selection is ConversionSelection => selection !== null),
      distinctUntilChanged(sameConversionSelection),
      switchMap((selection: ConversionSelection) => this.load(selection)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** Errors are caught here so one failed request never ends `vm$`; the next filter change fetches again. */
  private load(selection: ConversionSelection): Observable<ConversionMetricsVm> {
    return combineLatest([
      this.dashboardFacade.getConversionMetrics$(selection.range, selection.scope, selection.period),
      this.filtersFacade.now$,
    ]).pipe(
      map(([metrics, now]: [ConversionMetrics, Date]) => toDataVm(metrics, now)),
      startWith(LOADING_VM),
      catchError((error: unknown) => of(errorVm<ConversionMetricsDataVm>(error))),
    );
  }
}
